import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Shirt, Plus, Trash2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ITEM_LABELS = {
  full_set: "Full set (Top + Shorts + Socks)",
  jersey: "Top (Jersey)",
  shorts: "Shorts",
  socks: "Socks",
};
const ALL_ITEM_TYPES = ["full_set", "jersey", "shorts", "socks"];

/**
 * Kit step for registration checkout (parent + admin flows).
 * Props:
 *  - termId: string
 *  - childrenList: [{ key, name, playerId? }]
 *  - onChange: (kitSelections, extrasTotal) => void
 */
export const KitSelectionStep = ({ termId, childrenList = [], onChange }) => {
  const [ctx, setCtx] = useState(null);
  const [applyToAll, setApplyToAll] = useState(true);
  // per-child state keyed by child.key
  const [state, setState] = useState({});
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastSigRef = useRef("");

  // Fetch context (bundle + pricing + already-has-kit)
  useEffect(() => {
    if (!termId) return;
    const pids = childrenList.map((c) => c.playerId).filter(Boolean).join(",");
    axios
      .get(`${API}/kit/registration-context?term_id=${encodeURIComponent(termId)}&player_ids=${encodeURIComponent(pids)}`, { withCredentials: true })
      .then((r) => setCtx(r.data))
      .catch(() => setCtx(null));
  }, [termId]);

  // Initialise per-child state once context arrives / children change
  useEffect(() => {
    if (!ctx) return;
    setState((prev) => {
      const next = { ...prev };
      childrenList.forEach((c) => {
        if (!next[c.key]) {
          // Returning either because a kit is already on record, or because this
          // child is not paying the registration fee that includes one.
          const returning = (c.playerId && ctx.already_has_kit && ctx.already_has_kit[c.playerId])
            || c.paidRegistrationFee === false;
          next[c.key] = { optOut: !!returning, returning: !!returning, freeSizes: {}, extras: [] };
        }
      });
      // prune removed children
      Object.keys(next).forEach((k) => {
        if (!childrenList.find((c) => c.key === k)) delete next[k];
      });
      return next;
    });
  }, [ctx, childrenList]);

  const bundleTypes = ctx ? Array.from(new Set((ctx.included_kit_items || []).map((i) => i.item_type))) : [];
  const sizes = ctx?.available_sizes || [];
  const pricing = ctx?.pricing || {};

  // Emit kit_selections + extras total whenever state changes
  useEffect(() => {
    if (!ctx) return;
    const selections = {};
    let extrasTotal = 0;
    childrenList.forEach((c) => {
      const st = state[c.key];
      if (!st) return;
      (st.extras || []).forEach((ex) => {
        extrasTotal += (Number(pricing[ex.item_type]) || 0) * (Number(ex.qty) || 0);
      });
      selections[(c.name || "").trim().toLowerCase()] = {
        child_name: c.name,
        already_has_kit: !!st.optOut,
        free_bundle_sizes: st.optOut ? {} : st.freeSizes,
        extras: (st.extras || []).filter((e) => e.item_type && e.qty > 0),
      };
    });
    const sig = JSON.stringify({ selections, extrasTotal });
    if (sig === lastSigRef.current) return;
    lastSigRef.current = sig;
    onChangeRef.current && onChangeRef.current(selections, Math.round(extrasTotal * 100) / 100);
  }, [state, ctx, childrenList]);

  // Set a free-bundle size; when applyToAll, prefill empty same-type fields across children
  const setFreeSize = useCallback((childKey, itemType, size) => {
    setState((prev) => {
      const next = { ...prev };
      next[childKey] = { ...next[childKey], freeSizes: { ...next[childKey].freeSizes, [itemType]: size } };
      if (applyToAll && size) {
        Object.keys(next).forEach((k) => {
          if (next[k].optOut) return;
          if (!next[k].freeSizes?.[itemType]) {
            next[k] = { ...next[k], freeSizes: { ...next[k].freeSizes, [itemType]: size } };
          }
          // also prefill empty extras of same type
          next[k] = {
            ...next[k],
            extras: (next[k].extras || []).map((ex) => (ex.item_type === itemType && !ex.size ? { ...ex, size } : ex)),
          };
        });
      }
      return next;
    });
  }, [applyToAll]);

  const setOptOut = (childKey, val) =>
    setState((prev) => ({ ...prev, [childKey]: { ...prev[childKey], optOut: val } }));

  const addExtra = (childKey) =>
    setState((prev) => ({
      ...prev,
      [childKey]: { ...prev[childKey], extras: [...(prev[childKey].extras || []), { item_type: "full_set", size: "", qty: 1 }] },
    }));

  const updateExtra = (childKey, idx, field, value) =>
    setState((prev) => {
      const extras = [...(prev[childKey].extras || [])];
      extras[idx] = { ...extras[idx], [field]: field === "qty" ? Math.max(1, parseInt(value) || 1) : value };
      return { ...prev, [childKey]: { ...prev[childKey], extras } };
    });

  const removeExtra = (childKey, idx) =>
    setState((prev) => {
      const extras = [...(prev[childKey].extras || [])];
      extras.splice(idx, 1);
      return { ...prev, [childKey]: { ...prev[childKey], extras } };
    });

  if (!ctx) {
    return <div className="text-sm text-slate-400" data-testid="kit-step-loading">Loading kit options…</div>;
  }

  const selectCls = "bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white w-full";

  return (
    <div className="space-y-5" data-testid="kit-selection-step">
      <div className="flex items-center gap-2 text-slate-200">
        <Shirt className="w-5 h-5 text-fuchsia-400" />
        <h3 className="text-lg font-semibold">Kit</h3>
      </div>

      {bundleTypes.length > 0 && (
        <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer" data-testid="kit-apply-all-toggle">
          <Checkbox checked={applyToAll} onCheckedChange={(v) => setApplyToAll(!!v)} />
          Apply the first size I pick to all items of the same type
        </label>
      )}

      {childrenList.map((c) => {
        const st = state[c.key] || { freeSizes: {}, extras: [], optOut: false };
        const extrasSubtotal = (st.extras || []).reduce(
          (s, ex) => s + (Number(pricing[ex.item_type]) || 0) * (Number(ex.qty) || 0), 0
        );
        return (
          <div key={c.key} className="bg-slate-900/40 border border-slate-700 rounded-lg p-4 space-y-4" data-testid={`kit-child-${c.key}`}>
            <div className="font-medium text-white">{c.name}</div>

            {/* Free bundle */}
            {st.returning ? (
              <div className="flex items-center gap-2 text-sm text-emerald-300" data-testid={`kit-returning-${c.key}`}>
                <CheckCircle2 className="w-4 h-4" />
                {c.paidRegistrationFee === false
                  ? "No registration fee on this child, so no new kit is included."
                  : "Already has a kit on record — no new kit needed."}
              </div>
            ) : bundleTypes.length > 0 ? (
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer" data-testid={`kit-optout-${c.key}`}>
                  <Checkbox checked={!!st.optOut} onCheckedChange={(v) => setOptOut(c.key, v)} />
                  I already have my kit — skip the included kit
                </label>
                {!st.optOut && (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-400">Included free with registration — pick a size:</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {bundleTypes.map((it) => (
                        <div key={it}>
                          <Label className="text-xs text-slate-300 mb-1 block">{ITEM_LABELS[it] || it}</Label>
                          <select
                            className={selectCls}
                            value={st.freeSizes?.[it] || ""}
                            onChange={(e) => setFreeSize(c.key, it, e.target.value)}
                            data-testid={`kit-free-size-${c.key}-${it}`}
                          >
                            <option value="">Select size…</option>
                            {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            {/* Paid extras */}
            <div className="space-y-2 pt-2 border-t border-slate-800">
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-400">Add extra kit (paid)</p>
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => addExtra(c.key)} data-testid={`kit-add-extra-${c.key}`}>
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              {(st.extras || []).map((ex, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`kit-extra-${c.key}-${idx}`}>
                  <div className="col-span-5">
                    <select className={selectCls} value={ex.item_type} onChange={(e) => updateExtra(c.key, idx, "item_type", e.target.value)} data-testid={`kit-extra-type-${c.key}-${idx}`}>
                      {ALL_ITEM_TYPES.map((t) => <option key={t} value={t}>{(ITEM_LABELS[t] || t)} (${pricing[t] ?? 0})</option>)}
                    </select>
                  </div>
                  <div className="col-span-4">
                    <select className={selectCls} value={ex.size || ""} onChange={(e) => updateExtra(c.key, idx, "size", e.target.value)} data-testid={`kit-extra-size-${c.key}-${idx}`}>
                      <option value="">Size…</option>
                      {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <input type="number" min={1} className={selectCls} value={ex.qty} onChange={(e) => updateExtra(c.key, idx, "qty", e.target.value)} data-testid={`kit-extra-qty-${c.key}-${idx}`} />
                  </div>
                  <div className="col-span-1">
                    <button type="button" onClick={() => removeExtra(c.key, idx)} className="text-slate-400 hover:text-red-400" data-testid={`kit-extra-remove-${c.key}-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {extrasSubtotal > 0 && (
                <p className="text-right text-sm text-fuchsia-300 font-medium" data-testid={`kit-extra-subtotal-${c.key}`}>
                  Kit add-ons: ${extrasSubtotal.toFixed(2)}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default KitSelectionStep;
